import { NgIf, NgStyle } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { BoondockFacade } from '@curbnturf/boondock-listing/src/lib/+state/boondock.facade';
import { CategorySvgComponent, SiteSvgsComponent } from '@curbnturf/custom-svgs';
import {
  AmenityType,
  BoondockPoint,
  DEFAULT_IMAGE_URL,
  HelperFunctions,
  IAmenityType,
  IRoutePoint,
  User,
} from '@curbnturf/entities';
import { TripPlannerFacade } from '@curbnturf/trip-planner/src/lib/+state/trip-planner.facade';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { IonicModule } from '@ionic/angular';
import { Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'curbnturf-boondock-search-display-card',
  templateUrl: './boondock-search-display-card.component.html',
  styleUrls: ['./boondock-search-display-card.component.scss'],
  imports: [NgIf, NgStyle, IonicModule, SiteSvgsComponent, CategorySvgComponent],
})
export class BoondockSearchDisplayCardComponent implements OnChanges, OnInit, OnDestroy {
  @Input() point: BoondockPoint;
  @Input() selected: boolean;
  @Input() defaultPhoto: string;
  @Input() updateRoute: boolean = true;

  @Output() closeBubble: EventEmitter<void> = new EventEmitter<void>();

  name: string;

  photoUrl: string = '';

  user: User;

  subscriptions: Subscription[] = [];

  get favorite(): boolean {
    return !!this.user?.boondockFavorites?.find((favorite) => favorite.boondockId === this.point?.id);
  }

  get addedToTrip(): boolean {
    if (this.trip.length > 0 && this.point?.id) {
      const boondockFound = this.trip.find((el) => {
        if (el.isListing && this.point?.id && el.boondock?.id) {
          return el.boondock?.id === this.point.id;
        }
        return undefined;
      });
      if (boondockFound) {
        return true;
      }
    }
    return false;
  }

  private trip: IRoutePoint[] = [];

  constructor(
    private cd: ChangeDetectorRef,
    public element: ElementRef,
    private boondockFacade: BoondockFacade,
    private tripPlannerFacade: TripPlannerFacade,
    private userFacade: UserFacade,
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.userFacade.currentUser$.subscribe((user) => {
        this.user = user;
        this.cd.detectChanges();
      }),
      this.tripPlannerFacade.trip$.subscribe((trip) => {
        if (trip && trip.length > 0) {
          this.trip = trip;
        } else {
          this.trip = [];
        }
        this.cd.detectChanges();
      }),
    );
  }

  ngOnChanges() {
    if (this.point) {
      this.name = this.point.name?.slice(0, 32);

      if (this.point.photos && this.point.photos.length > 0) {
        this.photoUrl = HelperFunctions.getPhotoUrl3x4Small(this.point.photos[0]);
      } else {
        this.photoUrl = this.defaultPhoto?.length ? this.defaultPhoto : DEFAULT_IMAGE_URL;
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  amenityName(amenity: IAmenityType['type']): string {
    if (!amenity) {
      return '';
    }

    if (amenity) {
      return AmenityType[amenity];
    }

    return '';
  }

  onFavorite(event: Event) {
    event.stopPropagation();

    if (this.favorite) {
      this.boondockFacade.unfavorite(new BoondockPoint({ ...this.point }));
    } else {
      this.boondockFacade.favorite(new BoondockPoint({ ...this.point }));
    }
  }

  onAddToTrip(event: Event) {
    event.stopPropagation();
    const point: IRoutePoint = {
      isBoondock: true,
      boondock: {
        id: this.point.id,
        coordinates: {
          ...this.point.coordinates,
        },
        name: this.point.name,
        address: this.point.address,
        photos: this.point.photos,
        rating: this.point.rating,
        ratingCount: this.point.ratingCount,
      },
      name: this.point.name,
      coordinates: { ...this.point.coordinates },
      isActive: true,
    };
    this.tripPlannerFacade.addPoint(point, this.updateRoute);
    this.closeBubble.emit();
  }

  onRemoveFromTrip(event: Event) {
    event.stopPropagation();
    const foundPoint = this.trip.find((el) => el.boondock?.id === this.point?.id);
    if (foundPoint) {
      this.tripPlannerFacade.removePoint(foundPoint);
    }
    this.closeBubble.emit();
  }
}
